import { parseCardId, RANK_ACE, SUITS, type CardId, type Seat, type Suit } from "@bing/engine-core";
import type { TrickPlay } from "./types";

/**
 * The three SUIT-LESS calls the Hakem may name instead of a خال — the Iranian
 * «حکم بی‌خال» family. An Iranian Hokm app's own rulebook states the closed set
 * outright: «۷ حکم داریم: خاج، خشت، دل، پیک، سَرَس(saras)، نَرَس(naras)، تَکْ نَرَس»
 * (saeid-shirazi.blogfa.com/post/305) — the four suits plus these three.
 *
 * In ALL THREE there is no trump, so there is no برش (ruffing): a card of another
 * suit «ارزش صفر» — worth nothing — «حتی اگر تک باشند» (ibid). Follow-suit is
 * unchanged. What differs between them is only the ORDER cards of the LED SUIT are
 * ranked in (see {@link trickStrength}):
 *
 *  • `sares`   — «سرس»    the ordinary order, Ace high:  A K Q J 10 … 3 2
 *  • `nares`   — «نرس»    inverted, the LOWEST takes it: 2 3 4 … J Q K A
 *  • `asnares` — «آس نرس» نرس with the Ace promoted:      A 2 3 4 … J Q K
 *
 * (Also written «تک‌نرس». boresh.ca/hokmrules gives all three orders verbatim.)
 *
 * ⚠️ ONE SOURCE DISAGREES ON `sares` and is recorded here so nobody "fixes" it by
 * accident: bakht.org ranks سرس King-high with the Ace LOWEST, and calls the
 * Ace-high version a fourth mode «آس سرس». Four independent lineages (boresh.ca,
 * blogfa, gameboxonline.ir, gameofcard) give the Ace-high order above, which is
 * what this engine implements — an operator decision, 2026-08-25.
 */
export const HOKM_TRUMP_MODES = ["sares", "nares", "asnares"] as const;
export type HokmTrumpMode = (typeof HOKM_TRUMP_MODES)[number];

/** What the Hakem called this hand: one of the four suits, or a suit-less mode. */
export type HokmTrump = Suit | HokmTrumpMode;

/**
 * Every call the Hakem may make, in PICKER ORDER — the four suits first, then the
 * three named modes. `legalMoves` maps straight off this, so the client's picker
 * order is the server's order and the two can never drift.
 */
export const HOKM_TRUMP_CALLS: readonly HokmTrump[] = [...SUITS, ...HOKM_TRUMP_MODES];

const TRUMP_MODE_SET: ReadonlySet<string> = new Set<string>(HOKM_TRUMP_MODES);

/** Is this call one of the suit-less named modes (i.e. is there no trump this hand)? */
export function isTrumpMode(call: HokmTrump): call is HokmTrumpMode {
  return TRUMP_MODE_SET.has(call);
}

/** Tricks a team must win to take the hand. */
export const TRICKS_TO_WIN = 7;
/** Match points for a normal hand win. */
export const NORMAL_POINTS = 1;
/** Match points for a کوت (7–0 sweep) — double. */
export const KOT_POINTS = 2;
/**
 * Match points for a حاکم کوت — a کوت whose SWEPT team is the Hakem's own.
 *
 * ATTESTED BY THREE INDEPENDENT IRANIAN LINEAGES, all agreeing on the number:
 *   · fa.wikipedia.org «حکم (بازی)» — «اگر تیم کت شونده تیم حاکم باشد سه باخت و اگر تیم کت شونده
 *     حاکم نباشد دو باخت در نظر گرفته می‌شود.»
 *   · bazi.wiki «حکم (بازی ورق)» — «حاکم کوت: برد یک کوت در برابر تیم یا بازیکن حاکم»,
 *     «در ایران: ۲ امتیاز برای کوت، یا ۳ امتیاز برای حاکم کوتی».
 *   · persianbt.com/616 — the same rule under the OTHER common name, «پرکوت»: a 7–0 by the team
 *     the Hakem is NOT on pays «۳ امتیاز» rather than the ordinary کوت's 2.
 * (The two names describe one rule; nothing found sets a different number for either.)
 */
export const HAKEM_KOT_POINTS = 3;

// There are only 52 distinct card ids, so memoize the parse — suit/rank are on
// the hot path (legality + trick resolution run many times per move).
const SUIT_CACHE = new Map<CardId, Suit>();
const RANK_CACHE = new Map<CardId, number>();

export function suitOf(id: CardId): Suit {
  let suit = SUIT_CACHE.get(id);
  if (suit === undefined) {
    suit = parseCardId(id).suit;
    SUIT_CACHE.set(id, suit);
  }
  return suit;
}

/** Numeric rank: Ace is high (14) down to 2, straight from the core scale. */
export function rankOf(id: CardId): number {
  let rank = RANK_CACHE.get(id);
  if (rank === undefined) {
    rank = parseCardId(id).rank;
    RANK_CACHE.set(id, rank);
  }
  return rank;
}

/** The two seats forming a partnership: team 0 = seats 0&2, team 1 = seats 1&3. */
export function teamOf(seat: Seat): 0 | 1 {
  return (seat % 2) as 0 | 1;
}

/**
 * Hakem selection for the first hand of a match: cards are dealt face-up one at
 * a time around the table (seat 0, 1, 2, 3, 0, …) off `dealOrder`, and the seat
 * that receives the **first Ace** becomes the Hakem. Pure and deterministic in
 * the deal order, so a seeded shuffle makes the choice reproducible.
 */
export function firstAceSeat(dealOrder: readonly CardId[], seats: number): Seat {
  const idx = dealOrder.findIndex((id) => rankOf(id) === RANK_ACE);
  return (idx >= 0 ? idx : 0) % seats;
}

/**
 * The cards `hand` may legally play into `trick`. Hokm's only restriction is
 * **follow suit if able**: when the trick is led you may play anything; when
 * following you must play the led suit if you hold it, otherwise you are free
 * to trump or discard (there is no obligation to trump, and no revoke).
 */
export function legalPlays(hand: readonly CardId[], trick: readonly TrickPlay[]): CardId[] {
  if (trick.length === 0) return [...hand];
  const ledSuit = suitOf(trick[0]!.card);
  const inSuit = hand.filter((c) => suitOf(c) === ledSuit);
  return inSuit.length > 0 ? inSuit : [...hand];
}

/**
 * How strongly `card` competes UNDER `call` — higher takes the trick. Only ever
 * compared between cards that are already CONTENDERS for the same trick (all
 * trumps, or all of the led suit), so it never has to order two different suits.
 *
 * For a SUIT call — and for سرس, which is the ordinary order with no trump — this
 * is EXACTLY {@link rankOf}. That identity is what makes every suit hand play
 * byte-for-byte as it did before the named modes existed, in the engine AND in the
 * bot (which compares cards through this same function).
 */
export function trickStrength(call: HokmTrump, card: CardId): number {
  const rank = rankOf(card);
  // نرس — the LOWEST card of the led suit takes it, so the order is simply
  // reversed: 2 is the strongest (-2) and the Ace the weakest (-14).
  if (call === "nares") return -rank;
  // آس نرس — نرس with the Ace promoted to the top: A > 2 > 3 > … > K. Zero sits
  // above every negative value, so the Ace outranks the 2 and nothing else moves.
  if (call === "asnares") return rank === RANK_ACE ? 0 : -rank;
  return rank;
}

/**
 * Winner of a (partial or complete) trick under `call`: the strongest trump if any
 * trump was played, otherwise the strongest card of the led suit — «strongest»
 * being {@link trickStrength}, so نرس/آس نرس invert it. A suit-less call has NO
 * trump, so the led suit alone ever decides it: an off-suit card is «ارزش صفر»
 * even when it is that mode's own best card («حتی اگر دو باشند» — blogfa).
 *
 * `contenders` can never be empty: the LEADER played the led suit by definition.
 */
export function trickWinner(trick: readonly TrickPlay[], call: HokmTrump): Seat {
  const ledSuit = suitOf(trick[0]!.card);
  const trumps: readonly TrickPlay[] = isTrumpMode(call)
    ? []
    : trick.filter((p) => suitOf(p.card) === call);
  const contenders = trumps.length > 0 ? trumps : trick.filter((p) => suitOf(p.card) === ledSuit);
  let best = contenders[0]!;
  let bestStrength = trickStrength(call, best.card);
  for (const play of contenders) {
    const strength = trickStrength(call, play.card);
    if (strength > bestStrength) {
      best = play;
      bestStrength = strength;
    }
  }
  return best.seat;
}

/** Tricks won per seat this hand, derived from the `taken` piles (4 cards / trick). */
export function trickCountsBySeat(taken: readonly (readonly CardId[])[]): number[] {
  return taken.map((cards) => cards.length / 4);
}

/** Tricks won per team this hand, `[team0, team1]`. */
export function teamTrickCounts(taken: readonly (readonly CardId[])[]): [number, number] {
  const counts: [number, number] = [0, 0];
  taken.forEach((cards, seat) => {
    counts[teamOf(seat)] += cards.length / 4;
  });
  return counts;
}

export interface HandOutcome {
  /** Team that reached {@link TRICKS_TO_WIN} first. */
  readonly winningTeam: number;
  readonly kot: boolean;
  /** A حاکم کوت: the swept team is the HAKEM's own, so the sweep pays three. Implies {@link kot}. */
  readonly hakemKot: boolean;
  readonly points: number;
}

/**
 * Does a 7–0 sweep pay a کوت under this call?
 *
 * A SUIT call ALWAYS pays. Under the three suit-less calls it is a PER-TABLE HOST SETTING,
 * `modeKot`, DEFAULTING OFF — because the evidence is thinner than it first looked and the
 * field itself treats this as something the table agrees before play. It is written down
 * honestly here so nobody re-derives it from one page.
 *
 * ATTESTED, BUT BY ONE PUBLISHER. irgamestars.ir says it twice, and is the only source
 * anywhere that states the scoring consequence:
 *   «در این نوع حکم، کت‌ و حاکم کت‌ (در هر صورت برنده تنها یک امتیاز میگیرد) و برش وجود ندارد»
 *   «در این نوع حکم، کوت‌ و حاکم کوت‌ و برش وجود ندارد»
 * ⚠️ THAT SAME PAGE CONTRADICTS ITSELF ~50 lines later («بردن تمام دست‌ها … امتیاز ویژه دارد»).
 *
 * ⚠️ آس نرس IS NOT ATTESTED EITHER WAY. Both sentences above are anchored on نرس alone;
 * NO source says آس نرس has no کوت, and none says it does. What is true is that no source
 * ever splits it off — the two that name all three together group them identically. So
 * applying the rule to it is a READING of the family, not a quoted rule.
 *
 * ⚠️ AND TWO SOURCES DISAGREE IN KIND — WHICH IS WHY THIS IS A SETTING RATHER THAN A RULE:
 * saeid-shirazi.blogfa.com/post/305 and gameofcard.home.blog make کوت under these calls a
 * PRE-GAME AGREEMENT, not an absence — «آیا این حکم ها "کوتی" دارند یا خیر؟» — and the first
 * ships a real Hokm app with a `kot` CHECKBOX («کوتی داشتن یا نداشتن حکم های سرس نرس تک نرس»).
 * So the platform offers the same choice: OFF by default (the reading irgamestars.ir states,
 * and what shipped on 2026-08-25), ON if the host says so.
 *
 * ⚠️ A LIKELY ORIGIN OF THE BELIEF, worth not re-importing: search engines render the
 * Persian «خال حکم وجود ندارد» (there is no trump SUIT) into English as «there is no
 * kut/court», and English "cut" ↔ کوت ↔ کت collide. boresh.ca and bazigap, both quoted
 * for this rule, contain ZERO occurrences of کوت.
 *
 * A suit call is UNCHANGED: 7–0 still pays {@link KOT_POINTS}, whatever `modeKot` says.
 *
 * ⚠️ `modeKot` DEFAULTS TO FALSE, and every caller that omits it therefore gets exactly the
 * behaviour that shipped on 2026-08-25. That default is load-bearing twice over: it keeps a
 * table that never chose anything on the conventional reading, and it is what a LEGACY solo
 * `ActiveGame` row — persisted before the field existed, so it rehydrates with `modeKot`
 * absent — resumes as. A game must come back as the game it WAS.
 *
 * Kept as its own predicate (not inlined) because it is the one place the rule lives, and
 * `handOutcome` stays a pure function of (trick counts, call, setting).
 */
export function callPaysKot(call: HokmTrump, modeKot = false): boolean {
  return !isTrumpMode(call) || modeKot;
}

/**
 * Resolve a finished hand from its per-team trick counts. Exactly one team has
 * reached {@link TRICKS_TO_WIN}; a 7–0 sweep (the other team on 0) is a کوت and
 * scores double — EXCEPT under a suit-less call on a table that has not turned کوت
 * on, where a sweep pays the ordinary {@link NORMAL_POINTS}. See {@link callPaysKot}.
 *
 * …and a کوت whose SWEPT team is the HAKEM's own is a حاکم کوت, worth
 * {@link HAKEM_KOT_POINTS} instead of {@link KOT_POINTS}. The Hakem picked the call and still
 * took nothing, so beating them costs more.
 *
 * ⚠️ حاکم کوت IS A STRICT RIDER ON کوت — IT HAS NO SETTING OF ITS OWN, AND MUST NOT GET ONE.
 * Wherever a sweep is a کوت, this asks the one extra question and nothing else; wherever a sweep
 * is NOT a کوت it cannot fire at all. That is what makes the three cases fall out with no branch
 * per table type: on a table whose suit-less calls pay کوت (`modeKot`) a سرس/نرس/آس نرس sweep of
 * the Hakem's team pays 3; on a table where they do not, the SAME sweep is not a کوت, so it pays
 * the ordinary 1 — and the four خال calls on that very table still pay 3, because a suit call
 * always pays a کوت. The one source that speaks to the suit-less family says exactly this, naming
 * both in the same breath: «در این نوع حکم، کوت‌ و حاکم کوت‌ و برش وجود ندارد» (irgamestars.ir,
 * quoted in full at {@link callPaysKot}).
 *
 * `kot` reports whether a کوت was actually AWARDED, so it can never disagree with
 * `points`: a 7–0 under نرس on a کوت-off table is `{ kot: false, points: 1 }`, not a
 * kot worth 1. `hakemKot` is the same promise one level up — it can only ever be true
 * alongside `kot`, and it is the only way `points` reaches {@link HAKEM_KOT_POINTS}. That keeps
 * the web's «کوت» banner and the hard bot's `evaluateHand` (which scores `lastHand.points` on a
 * kot) correct for free.
 *
 * ⚠️ `hakem` IS A REQUIRED ARGUMENT AND DELIBERATELY HAS NO DEFAULT, unlike `modeKot` — whose
 * default exists only to keep a state persisted before that field resumable. Every caller always
 * knows who the Hakem is, so a default here could only ever hide a caller that forgot to say.
 */
export function handOutcome(
  trickCounts: readonly [number, number],
  call: HokmTrump,
  hakem: Seat,
  modeKot = false,
): HandOutcome {
  const winningTeam = trickCounts[0] >= TRICKS_TO_WIN ? 0 : 1;
  const losingTeam = winningTeam === 0 ? 1 : 0;
  const kot = trickCounts[losingTeam] === 0 && callPaysKot(call, modeKot);
  const hakemKot = kot && teamOf(hakem) === losingTeam;
  return {
    winningTeam,
    kot,
    hakemKot,
    points: hakemKot ? HAKEM_KOT_POINTS : kot ? KOT_POINTS : NORMAL_POINTS,
  };
}
